import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Loader2, Plus, RefreshCw, UserPlus, XCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { setActiveAdminOrg } from '@/lib/admin/useAdminGuard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';

type TenantRow = {
  org_id: string;
  org_name: string;
  customer_slug: string;
  admin_count: number;
  latest_invitation_id: string | null;
  latest_invitation_email: string | null;
  latest_invitation_status: string | null;
  latest_invitation_created_at: string | null;
  latest_invitation_expires_at: string | null;
  created_at: string;
};

type RpcInviteResult =
  | { ok: true; invitation_id: string }
  | { ok: false; error: string };

type RpcEnqueueResult =
  | { ok: true }
  | { ok: false; error: string };

type Status = 'claimed' | 'revoked' | 'pending' | 'expired' | 'none';

function deriveStatus(row: TenantRow): Status {
  if (row.admin_count > 0) return 'claimed';
  const s = (row.latest_invitation_status ?? '').toLowerCase();
  if (s === 'revoked') return 'revoked';
  if (s === 'expired') return 'expired';
  if (s === 'pending' || s === 'sent' || s === 'queued') return 'pending';
  return row.latest_invitation_id ? 'pending' : 'none';
}

function StatusBadge({ status }: { status: Status }) {
  const variant: 'default' | 'secondary' | 'destructive' | 'outline' =
    status === 'claimed' ? 'default'
    : status === 'revoked' || status === 'expired' ? 'destructive'
    : status === 'pending' ? 'secondary'
    : 'outline';
  return <Badge variant={variant} className="capitalize">{status}</Badge>;
}

function fmt(d: string | null): string {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Tenants() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: rows,
    error: queryError,
    isLoading,
  } = useQuery({
    queryKey: ['platform-tenants'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('platform_list_tenants');
      if (error) throw error;
      return ((data ?? []) as unknown) as TenantRow[];
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
  const loadError = queryError ? (queryError as Error).message : null;

  const [createOpen, setCreateOpen] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [rowBusy, setRowBusy] = useState<Record<string, boolean>>({});
  const [revokeTarget, setRevokeTarget] = useState<TenantRow | null>(null);

  const [inviteAdminTarget, setInviteAdminTarget] = useState<TenantRow | null>(null);
  const [inviteAdminEmail, setInviteAdminEmail] = useState('');
  const [inviteAdminBusy, setInviteAdminBusy] = useState(false);
  const [inviteAdminError, setInviteAdminError] = useState<string | null>(null);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
  }

  function resetCreate() {
    setOrgName('');
    setAdminEmail('');
    setCreateError(null);
    setCreateBusy(false);
  }

  async function enqueueInvite(invitationId: string): Promise<{ ok: true } | { ok: false; error: string }> {
    const { data, error } = await supabase.rpc('enqueue_org_admin_invite', {
      p_invitation_id: invitationId,
    });
    if (error) return { ok: false, error: error.message };
    const res = (data ?? { ok: false, error: 'No response.' }) as RpcEnqueueResult;
    if (res.ok !== true) return { ok: false, error: res.error };
    return { ok: true };
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (createBusy) return;
    const name = orgName.trim();
    const email = adminEmail.trim();
    if (name.length < 2) { setCreateError('Org name must be at least 2 characters.'); return; }
    if (!EMAIL_RE.test(email)) { setCreateError('Enter a valid email address.'); return; }
    setCreateBusy(true);
    setCreateError(null);
    const { data: bootData, error: bootErr } = await supabase.rpc('bootstrap_org', {
      p_org_name: name,
      p_admin_email: email,
      p_invitation_ttl_days: 14,
    });
    if (bootErr) {
      setCreateError(bootErr.message);
      setCreateBusy(false);
      return;
    }
    const boot = (bootData ?? { ok: false, error: 'No response.' }) as RpcInviteResult;
    if (boot.ok !== true) {
      setCreateError(boot.error);
      setCreateBusy(false);
      return;
    }
    const enqRes = await enqueueInvite(boot.invitation_id);
    if (enqRes.ok !== true) {
      setCreateError(enqRes.error);
      setCreateBusy(false);
      return;
    }
    toast({ title: 'Tenant created', description: `Invite sent to ${email}.` });
    setCreateOpen(false);
    resetCreate();
    invalidate();
  }

  async function handleResend(row: TenantRow) {
    if (!row.latest_invitation_id || rowBusy[row.org_id]) return;
    setRowBusy((s) => ({ ...s, [row.org_id]: true }));
    const { data: resData, error: resErr } = await supabase.rpc('platform_resend_invite', {
      p_invitation_id: row.latest_invitation_id,
      p_ttl_days: 7,
    });
    if (resErr) {
      setRowBusy((s) => ({ ...s, [row.org_id]: false }));
      toast({ title: 'Resend failed', description: resErr.message, variant: 'destructive' });
      return;
    }
    const res = (resData ?? { ok: false, error: 'No response.' }) as RpcInviteResult;
    if (res.ok !== true) {
      setRowBusy((s) => ({ ...s, [row.org_id]: false }));
      toast({ title: 'Resend failed', description: res.error, variant: 'destructive' });
      return;
    }
    const enqRes = await enqueueInvite(res.invitation_id);
    setRowBusy((s) => ({ ...s, [row.org_id]: false }));
    if (enqRes.ok !== true) {
      toast({ title: 'Resend failed', description: enqRes.error, variant: 'destructive' });
      return;
    }
    toast({ title: 'Invite resent', description: row.latest_invitation_email ?? row.org_name });
    invalidate();
  }

  async function handleRevokeConfirm() {
    const row = revokeTarget;
    if (!row) return;
    setRevokeTarget(null);
    if (!row.latest_invitation_id || rowBusy[row.org_id]) return;
    setRowBusy((s) => ({ ...s, [row.org_id]: true }));
    const { error } = await supabase.rpc('platform_revoke_invite', {
      p_invitation_id: row.latest_invitation_id,
    });
    setRowBusy((s) => ({ ...s, [row.org_id]: false }));
    if (error) {
      toast({ title: 'Revoke failed', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Invite revoked', description: row.org_name });
    invalidate();
  }

  async function handleOpen(row: TenantRow) {
    if (rowBusy[row.org_id]) return;
    setRowBusy((s) => ({ ...s, [row.org_id]: true }));
    const { error } = await supabase.rpc('platform_join_org_as_admin', { _org_id: row.org_id });
    setRowBusy((s) => ({ ...s, [row.org_id]: false }));
    if (error) {
      toast({ title: 'Could not open tenant', description: error.message, variant: 'destructive' });
      return;
    }
    setActiveAdminOrg(row.org_id);
    navigate('/app/admin');
  }

  async function handleInviteAdminSubmit(e: React.FormEvent) {
    e.preventDefault();
    const target = inviteAdminTarget;
    if (!target || inviteAdminBusy) return;
    const email = inviteAdminEmail.trim();
    if (!EMAIL_RE.test(email)) {
      setInviteAdminError('Enter a valid email address.');
      return;
    }
    setInviteAdminBusy(true);
    setInviteAdminError(null);
    const { data, error } = await supabase.rpc('platform_invite_org_admin', {
      _org_id: target.org_id,
      _email: email,
      _ttl_days: 14,
    });
    setInviteAdminBusy(false);
    if (error) {
      setInviteAdminError(error.message);
      return;
    }
    const res = (data ?? { ok: false, error: 'No response.' }) as
      | { ok: true; invitation_id: string; enqueue?: { ok: boolean; error?: string } }
      | { ok: false; error: string };
    if (res.ok !== true) {
      setInviteAdminError(res.error);
      return;
    }
    if (res.enqueue && res.enqueue.ok !== true) {
      setInviteAdminError(res.enqueue.error ?? 'Email enqueue failed.');
      return;
    }
    toast({ title: 'Admin invited', description: `${email} → ${target.org_name}` });
    setInviteAdminTarget(null);
    setInviteAdminEmail('');
    invalidate();
  }

  return (
    <div className="space-y-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Tenants</h1>
          <p className="text-sm text-muted-foreground">
            Create and manage CultureLens organizations.
          </p>
        </div>
        <Button onClick={() => { resetCreate(); setCreateOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" /> Create tenant
        </Button>
      </div>

      <div className="border border-border rounded-lg bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Org name</TableHead>
              <TableHead>Admin email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          {/* customer_slug column intentionally hidden: pulse_deployment has no per-org join key in schema, so platform_list_tenants returns NULL for it. */}
          <TableBody>
            {isLoading && Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={`sk-${i}`}>
                {Array.from({ length: 6 }).map((__, j) => (
                  <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                ))}
              </TableRow>
            ))}
            {!isLoading && loadError && (
              <TableRow>
                <TableCell colSpan={6} className="text-sm text-destructive py-6 text-center">
                  {loadError}
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !loadError && (rows ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-sm text-muted-foreground py-10 text-center">
                  No tenants yet. Click Create tenant to invite the first org admin.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !loadError && (rows ?? []).map((row) => {
              const status = deriveStatus(row);
              const busy = !!rowBusy[row.org_id];
              const canMutate = status === 'pending' && !!row.latest_invitation_id;
              return (
                <TableRow key={row.org_id}>
                  <TableCell className="font-medium">{row.org_name}</TableCell>
                  <TableCell>{row.latest_invitation_email ?? '—'}</TableCell>
                  <TableCell><StatusBadge status={status} /></TableCell>
                  <TableCell className="text-muted-foreground">{fmt(row.created_at)}</TableCell>
                  <TableCell className="text-muted-foreground">{fmt(row.latest_invitation_expires_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => handleOpen(row)}>
                        {busy
                          ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                          : <ExternalLink className="h-3.5 w-3.5 mr-1.5" />}
                        Open
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => {
                          setInviteAdminTarget(row);
                          setInviteAdminEmail('');
                          setInviteAdminError(null);
                        }}
                      >
                        <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                        Invite admin
                      </Button>
                      {canMutate && (
                        <>
                          <Button size="sm" variant="outline" disabled={busy} onClick={() => handleResend(row)}>
                            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                            Resend
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            disabled={busy}
                            onClick={() => setRevokeTarget(row)}
                          >
                            <XCircle className="h-3.5 w-3.5 mr-1.5" />
                            Revoke
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) resetCreate(); }}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Create new tenant</DialogTitle>
              <DialogDescription>
                Provisions a new organization and emails the first admin an invite. To invite a
                regular employee to an existing tenant, ask that tenant&apos;s admin to add them
                from the People page.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">Org name</Label>
                <Input
                  id="org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Acme Inc."
                  maxLength={200}
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-email">First admin email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="admin@acme.com"
                  maxLength={255}
                  required
                />
              </div>
              {createError && <p className="text-sm text-destructive">{createError}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)} disabled={createBusy}>
                Cancel
              </Button>
              <Button type="submit" disabled={createBusy}>
                {createBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create & send invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => { if (!o) setRevokeTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke invite?</AlertDialogTitle>
            <AlertDialogDescription>
              This invalidates the pending invite for{' '}
              <span className="font-medium">{revokeTarget?.org_name}</span>. The recipient will no
              longer be able to claim admin access via the existing link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleRevokeConfirm}
            >
              Revoke invite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!inviteAdminTarget}
        onOpenChange={(o) => {
          if (!o) {
            setInviteAdminTarget(null);
            setInviteAdminEmail('');
            setInviteAdminError(null);
            setInviteAdminBusy(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleInviteAdminSubmit}>
            <DialogHeader>
              <DialogTitle>Invite admin</DialogTitle>
              <DialogDescription>
                Send an admin invite for{' '}
                <span className="font-medium">{inviteAdminTarget?.org_name}</span>. The recipient
                will become an additional admin of this tenant once they accept.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="invite-admin-email">Admin email</Label>
                <Input
                  id="invite-admin-email"
                  type="email"
                  value={inviteAdminEmail}
                  onChange={(e) => setInviteAdminEmail(e.target.value)}
                  placeholder="admin@acme.com"
                  maxLength={255}
                  required
                  autoFocus
                />
              </div>
              {inviteAdminError && (
                <p className="text-sm text-destructive">{inviteAdminError}</p>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setInviteAdminTarget(null)}
                disabled={inviteAdminBusy}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={inviteAdminBusy}>
                {inviteAdminBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Send admin invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
